import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import AssetReturnReminders from './AssetReturnReminders';
import { Save, X, Plus, RefreshCw, RotateCcw, PlayCircle, Calendar as CalendarIcon, Link2, Unlink, History, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RemindersSettings — admin config for the automated document-
 * expiry reminder scanner. Three sections:
 *  1. Channels (email / dashboard / calendar)
 *  2. Recipients (employee / manager / admin tiers)
 *  3. Document types × per-type thresholds
 * Plus a manual "Run scan now" button so admins can verify the
 * pipeline without waiting for tomorrow's 08:15 UTC cron.
 */
const RemindersSettings = ({ canEdit = true }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [newThreshold, setNewThreshold] = useState({}); // { doc_key: '' }
  const [calStatus, setCalStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [history, setHistory] = useState({ total: 0, items: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({ channel: '', only_failed: false });
  const [invScanning, setInvScanning] = useState(false);
  const [dayInput, setDayInput] = useState('');

  const saveInvoice = (patch) => save({ invoice_reminders: patch });

  const runInvoiceScan = async () => {
    setInvScanning(true);
    try {
      const { data } = await axios.post(`${API}/reminders/scan-invoices`);
      if (data.skipped) toast.info(`Scan skipped (${data.skipped})`);
      else toast.success(`Sent ${data.drafts_sent || 0} draft reminder(s) and ${data.uninvoiced_sent || 0} unbilled-hours alert(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invoice scan failed');
    } finally { setInvScanning(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, cs] = await Promise.all([
        axios.get(`${API}/reminders/settings`),
        axios.get(`${API}/calendar/status`).catch(() => ({ data: null })),
      ]);
      setSettings(s.data);
      setDayInput(((s.data?.invoice_reminders?.days_of_month) || [0, 1]).join(','));
      setCalStatus(cs.data);
    } catch {
      toast.error(t('auto.set.failedToLoadReminderSettings'));
    } finally { setLoading(false); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (historyFilter.channel) params.set('channel', historyFilter.channel);
      if (historyFilter.only_failed) params.set('only_failed', 'true');
      const { data } = await axios.get(`${API}/reminders/history?${params}`);
      setHistory(data);
    } catch {
      toast.error(t('auto.set.failedToLoadReminderHistory'));
    } finally { setHistoryLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadHistory(); }, [historyFilter.channel, historyFilter.only_failed]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/reminders/settings`, patch);
      setSettings({
        ...data,
        document_catalogue: settings?.document_catalogue || data.document_catalogue,
      });
      toast.success(t('auto.set.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleChannel = (key, value) => save({ channels: { ...settings.channels, [key]: value } });
  const toggleRecipient = (key, value) => save({ recipients: { ...settings.recipients, [key]: value } });
  const toggleDocType = (key, value) => {
    const cfg = settings.document_types[key] || { enabled: true, days_before: [90, 30] };
    save({ document_types: { [key]: { ...cfg, enabled: value } } });
  };
  const setDocThresholds = (key, days_before) => {
    const cfg = settings.document_types[key] || { enabled: true, days_before: [90, 30] };
    save({ document_types: { [key]: { ...cfg, days_before } } });
  };

  const addThreshold = (key) => {
    const raw = (newThreshold[key] || '').trim();
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1 || n > 3650) {
      toast.error(t('auto.set.enterAPositiveNumberOfDays'));
      return;
    }
    const cfg = settings.document_types[key] || { enabled: true, days_before: [] };
    if (cfg.days_before.includes(n)) {
      toast.error(`${n} days is already configured`);
      return;
    }
    const next = [...cfg.days_before, n].sort((a, b) => b - a); // descending: 90, 30, 7
    setDocThresholds(key, next);
    setNewThreshold(prev => ({ ...prev, [key]: ''  }));
  };

  const removeThreshold = (key, day) => {
    const cfg = settings.document_types[key] || { enabled: true, days_before: [] };
    setDocThresholds(key, cfg.days_before.filter(d => d !== day));
  };

  const resetDefaults = async () => {
    if (!window.confirm(t('auto.set.resetAllReminderSettingsToFactory'))) return;
    setSaving(true);
    try {
      await axios.post(`${API}/reminders/reset-defaults`);
      toast.success(t('auto.set.resetToDefaults'));
      await load();
    } catch {
      toast.error(t('auto.set.resetFailed'));
    } finally { setSaving(false); }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${API}/reminders/scan`);
      toast.success(`Scan complete — ${data.reminders_fired} reminder(s) fired across ${data.scanned_employees} employees.`);
      loadHistory();
    } catch {
      toast.error(t('auto.set.scanFailed'));
    } finally { setScanning(false); }
  };

  const connectCalendar = async () => {
    try {
      const { data } = await axios.get(`${API}/calendar/connect`);
      if (data.authorization_url) {
        // Open OAuth in the current tab — Google will bounce back to
        // /api/oauth/calendar/callback which redirects to
        // /?calendar_connect=ok, at which point the Settings page
        // reloads and re-renders with the connected state.
        window.location.href = data.authorization_url;
      } else {
        toast.error(t('auto.set.failedToStartOauthFlow'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start OAuth flow');
    }
  };

  const disconnectCalendar = async () => {
    if (!window.confirm(t('auto.set.disconnectGoogleCalendarExistingEventsWill'))) return;
    try {
      await axios.delete(`${API}/calendar/disconnect`);
      toast.success(t('auto.set.googleCalendarDisconnected'));
      setCalStatus(null);
      // Also flip the channel off so we don't lie to the admin next scan.
      await axios.put(`${API}/reminders/settings`, { channels: { google_calendar: false } });
      await load();
    } catch { toast.error(t('auto.set.disconnectFailed')); }
  };

  const listCalendars = async () => {
    try {
      const { data } = await axios.get(`${API}/calendar/calendars`);
      setCalendars(data || []);
    } catch { toast.error(t('auto.set.failedToListCalendars')); }
  };

  const setTargetCalendar = async (calendarId) => {
    const chosen = calendars.find(c => c.id === calendarId);
    try {
      await axios.put(`${API}/calendar/target`, {
        target_calendar_id: calendarId,
        target_calendar_summary: chosen?.summary || '',
      });
      toast.success(`Publishing to: ${chosen?.summary || calendarId}`);
      await load();
    } catch { toast.error(t('auto.set.failedToSetCalendar')); }
  };

  // Auto-fetch calendar list once connected so the picker is
  // populated without an extra click.
  useEffect(() => {
    if (calStatus?.connected && calendars.length === 0) {
      listCalendars();
    }
  }, [calStatus?.connected]);

  if (loading || !settings) {
    return <div className="text-sm text-slate-500 p-4">{t('auto.set.loadingReminderSettings')}</div>;
  }

  const catalogue = settings.document_catalogue || [];
  const chan = settings.channels || {};
  const rec = settings.recipients || {};
  const inv = settings.invoice_reminders || {};

  return (
    <div className="space-y-6" data-testid="reminders-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{t('auto.set.expiryReminders')}</h3>
          <p className="text-sm text-slate-500">
            Automated alerts for passports, visas, work permits and certifications.
            The scanner runs daily at 08:15 UTC.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runScan}
            disabled={scanning}
            data-testid="reminders-run-scan-btn"
          >
            {scanning
              ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />{t('auto.set.scanning')}</>
              : <><PlayCircle className="w-3 h-3 mr-1" />{t('auto.set.runScanNow')}</>}
          </Button>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={resetDefaults}
              disabled={saving}
              data-testid="reminders-reset-defaults-btn"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              {t('auto.set.resetToDefaults')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="reminders-channels-card">
          <CardHeader>
            <CardTitle className="text-base">{t('auto.set.deliveryChannels')}</CardTitle>
            <CardDescription>{t('auto.set.howRemindersReachTheRecipients')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{t('auto.set.email')}</div>
                <div className="text-xs text-slate-500">{t('auto.set.usesYourConfiguredSmtpSettings')}</div>
              </div>
              <Switch
                checked={!!chan.email}
                onCheckedChange={(v) => canEdit && toggleChannel('email', v)}
                disabled={!canEdit || saving}
                data-testid="reminders-channel-email"
              />
            </div>
            <div
              className={`flex items-center justify-between pl-4 border-l-2 border-slate-200 ml-2 ${!chan.email ? 'opacity-40' : ''}`}
              data-testid="reminders-email-digest-row"
            >
              <div>
                <div className="text-sm font-medium">{t('auto.set.dailyDigestMode')}</div>
                <div className="text-xs text-slate-500">
                  Bundle all of a recipient&apos;s daily reminders into a single email instead of one email per document.
                </div>
              </div>
              <Switch
                checked={!!chan.email_digest}
                onCheckedChange={(v) => canEdit && chan.email && toggleChannel('email_digest', v)}
                disabled={!canEdit || saving || !chan.email}
                data-testid="reminders-channel-email-digest"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{t('auto.set.inAppDashboard')}</div>
                <div className="text-xs text-slate-500">Notification bell + Dashboard &quot;Expiring Soon&quot; card.</div>
              </div>
              <Switch
                checked={!!chan.dashboard}
                onCheckedChange={(v) => canEdit && toggleChannel('dashboard', v)}
                disabled={!canEdit || saving}
                data-testid="reminders-channel-dashboard"
              />
            </div>
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {t('auto.set.googleCalendar')}
                    {calStatus?.connected ? (
                      <Badge className="bg-green-100 text-green-800 border-green-300 text-[10px]">{t('auto.set.connected')}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">{t('auto.set.notConnected')}</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {calStatus?.connected
                      ? `Publishing as ${calStatus.account_email}`
                      : 'Publish reminders to a shared company calendar.'}
                  </div>
                </div>
                <Switch
                  checked={!!chan.google_calendar && !!calStatus?.connected}
                  onCheckedChange={(v) => canEdit && calStatus?.connected && toggleChannel('google_calendar', v)}
                  disabled={!canEdit || saving || !calStatus?.connected}
                  data-testid="reminders-channel-gcal"
                />
              </div>
              {canEdit && (
                <div className="mt-2 space-y-2">
                  {!calStatus?.connected ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={connectCalendar}
                      className="w-full"
                      data-testid="reminders-gcal-connect-btn"
                    >
                      <Link2 className="w-3 h-3 mr-1" />
                      Connect Google Calendar…
                    </Button>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-600 shrink-0">{t('auto.set.target')}</span>
                        <Select
                          value={calStatus.target_calendar_id || 'primary'}
                          onValueChange={setTargetCalendar}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid="reminders-gcal-target-select">
                            <SelectValue placeholder={t('auto.set.chooseCalendar')} />
                          </SelectTrigger>
                          <SelectContent>
                            {calendars.length === 0 && (
                              <SelectItem value="primary">{t('auto.set.primaryCalendar')}</SelectItem>
                            )}
                            {calendars.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.summary}{c.primary ? ' (primary)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" onClick={listCalendars} title={t('auto.set.refreshCalendars')}>
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={disconnectCalendar}
                        className="text-red-600 hover:text-red-700 w-full"
                        data-testid="reminders-gcal-disconnect-btn"
                      >
                        <Unlink className="w-3 h-3 mr-1" />
                        {t('auto.set.disconnect')}
                      </Button>
                    </>
                  )}
                  <div className="text-[11px] text-slate-500 leading-snug">
                    {t('auto.set.firstTimeInYourGoogleCloud')}
                    <code className="mx-1 px-1 bg-slate-100 rounded text-[10px]">/api/oauth/calendar/callback</code>
                    to the OAuth client&apos;s redirect URIs, and add the
                    <code className="mx-1 px-1 bg-slate-100 rounded text-[10px]">.../auth/calendar</code>
                    {t('auto.set.scopeOnTheOauthConsentScreen')}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="reminders-recipients-card">
          <CardHeader>
            <CardTitle className="text-base">{t('auto.set.recipientTiers')}</CardTitle>
            <CardDescription>{t('auto.set.whoReceivesAReminderWhenA')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{t('auto.set.employeeThemselves')}</div>
                <div className="text-xs text-slate-500">{t('auto.set.onlyFiresWhenTheEmployeeHas')}</div>
              </div>
              <Switch
                checked={!!rec.employee}
                onCheckedChange={(v) => canEdit && toggleRecipient('employee', v)}
                disabled={!canEdit || saving}
                data-testid="reminders-recipient-employee"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{t('auto.set.managers')}</div>
                <div className="text-xs text-slate-500">{t('auto.set.everyUserWithTheManagerRole')}</div>
              </div>
              <Switch
                checked={!!rec.manager}
                onCheckedChange={(v) => canEdit && toggleRecipient('manager', v)}
                disabled={!canEdit || saving}
                data-testid="reminders-recipient-manager"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{t('auto.set.admins')}</div>
                <div className="text-xs text-slate-500">{t('auto.set.everyUserWithTheAdminOr')}</div>
              </div>
              <Switch
                checked={!!rec.admin}
                onCheckedChange={(v) => canEdit && toggleRecipient('admin', v)}
                disabled={!canEdit || saving}
                data-testid="reminders-recipient-admin"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="reminders-invoice-card">
        <CardHeader>
          <CardTitle className="text-base">{t('auto.set.monthEndInvoiceReminders')}</CardTitle>
          <CardDescription>
            Nudges the back office about invoices still in draft and projects
            with hours that were never billed. Runs daily at 07:45 UTC and
            only fires on the day(s) of month configured below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">{t('auto.set.enabled')}</div>
              <div className="text-xs text-slate-500">{t('auto.set.masterSwitchForBothInvoiceScans')}</div>
            </div>
            <Switch
              checked={!!inv.enabled}
              onCheckedChange={(v) => canEdit && saveInvoice({ enabled: v })}
              disabled={!canEdit || saving}
              data-testid="invoice-reminders-enabled"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded border px-3 py-2">
              <div className="text-sm">{t('auto.set.draftInvoiceReminders')}</div>
              <Switch
                checked={!!inv.draft_reminders}
                onCheckedChange={(v) => canEdit && saveInvoice({ draft_reminders: v })}
                disabled={!canEdit || saving}
                data-testid="invoice-reminders-drafts"
              />
            </div>
            <div className="flex items-center justify-between rounded border px-3 py-2">
              <div className="text-sm">{t('auto.set.unbilledHoursAlerts')}</div>
              <Switch
                checked={!!inv.uninvoiced_alerts}
                onCheckedChange={(v) => canEdit && saveInvoice({ uninvoiced_alerts: v })}
                disabled={!canEdit || saving}
                data-testid="invoice-reminders-uninvoiced"
              />
            </div>
            <div className="flex items-center justify-between rounded border px-3 py-2">
              <div className="text-sm">{t('auto.set.inAppBell')}</div>
              <Switch
                checked={inv.channels?.dashboard !== false}
                onCheckedChange={(v) => canEdit && saveInvoice({ channels: { dashboard: v } })}
                disabled={!canEdit || saving}
                data-testid="invoice-reminders-channel-dashboard"
              />
            </div>
            <div className="flex items-center justify-between rounded border px-3 py-2">
              <div className="text-sm">{t('auto.set.email')}</div>
              <Switch
                checked={inv.channels?.email !== false}
                onCheckedChange={(v) => canEdit && saveInvoice({ channels: { email: v } })}
                disabled={!canEdit || saving}
                data-testid="invoice-reminders-channel-email"
              />
            </div>
          </div>
          <div>
            <div className="text-sm font-medium">{t('auto.set.daySOfMonth')}</div>
            <div className="text-xs text-slate-500 mb-1">
              Comma-separated. Use <code>0</code> for the last day of the month
              (e.g. <code>0,1</code> = month end and the 1st).
            </div>
            <div className="flex items-center gap-2">
              <Input
                className="w-40"
                value={dayInput}
                onChange={(e) => setDayInput(e.target.value)}
                placeholder="0,1"
                disabled={!canEdit || saving}
                data-testid="invoice-reminders-days"
              />
              <Button size="sm" variant="outline" disabled={!canEdit || saving}
                onClick={() => {
                  const days = dayInput.split(',').map(s => parseInt(s.trim(), 10))
                    .filter(n => Number.isFinite(n) && n >= 0 && n <= 31);
                  if (!days.length) { toast.error(t('auto.set.enterAtLeastOneDayBetween')); return; }
                  saveInvoice({ days_of_month: days });
                }}
                data-testid="invoice-reminders-days-save">
                <Save className="h-3.5 w-3.5 mr-1" /> {t('auto.set.saveDays')}
              </Button>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium">{t('auto.set.recipients')}</div>
            <div className="text-xs text-slate-500 mb-1">{t('auto.set.rolesThatReceiveTheInvoiceReminders')}</div>
            <div className="flex flex-wrap gap-2">
              {['admin', 'manager', 'accounts', 'auditor'].map(role => {
                const on = (inv.recipient_roles || []).includes(role);
                return (
                  <Button key={role} size="sm" variant={on ? 'default' : 'outline'}
                    disabled={!canEdit || saving}
                    onClick={() => {
                      const next = on
                        ? (inv.recipient_roles || []).filter(r => r !== role)
                        : [...(inv.recipient_roles || []), role];
                      saveInvoice({ recipient_roles: next });
                    }}
                    data-testid={`invoice-reminders-role-${role}`}>
                    {role}
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="pt-1">
            <Button size="sm" variant="outline" onClick={runInvoiceScan}
              disabled={!canEdit || invScanning} data-testid="invoice-reminders-run-now">
              <PlayCircle className="h-4 w-4 mr-1" />
              {invScanning ? t('auto.set.running') : t('auto.set.runInvoiceScanNow')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="reminders-doc-types-card">
        <CardHeader>
          <CardTitle className="text-base">{t('auto.set.documentsThresholds')}</CardTitle>
          <CardDescription>
            Each document type has its own list of &quot;days before expiry&quot; thresholds.
            The default is 90 days + 30 days. Add extra values for early warnings
            (e.g. 180 for passports, 7 for permits).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {catalogue.map(({ key, label }) => {
              const cfg = settings.document_types[key] || { enabled: true, days_before: [] };
              return (
                <div
                  key={key}
                  className="flex flex-col md:flex-row md:items-center gap-3 p-3 border rounded"
                  data-testid={`reminders-doc-row-${key}`}
                >
                  <div className="flex items-center gap-3 md:w-56">
                    <Switch
                      checked={!!cfg.enabled}
                      onCheckedChange={(v) => canEdit && toggleDocType(key, v)}
                      disabled={!canEdit || saving}
                      data-testid={`reminders-doc-toggle-${key}`}
                    />
                    <span className={`text-sm font-medium ${cfg.enabled ? '' : 'text-slate-400 line-through'}`}>{label}</span>
                  </div>
                  <div className="flex-1 flex items-center gap-2 flex-wrap">
                    {(cfg.days_before || []).length === 0 && (
                      <span className="text-xs text-slate-400 italic">{t('auto.set.noThresholdsThisDocumentWillNot')}</span>
                    )}
                    {(cfg.days_before || []).sort((a, b) => b - a).map(d => (
                      <span
                        key={d}
                        className="inline-flex items-center gap-1 bg-red-50 text-red-800 border border-red-200 rounded-full pl-3 pr-1 py-0.5 text-xs font-semibold"
                        data-testid={`reminders-threshold-${key}-${d}`}
                      >
                        {d}d
                        {canEdit && (
                          <button
                            onClick={() => removeThreshold(key, d)}
                            className="ml-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100 p-0.5"
                            title={`Remove ${d}-day threshold`}
                            data-testid={`reminders-threshold-remove-${key}-${d}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))}
                    {canEdit && (
                      <div className="flex items-center gap-1 ml-auto">
                        <Input
                          type="number"
                          min={1}
                          max={3650}
                          placeholder={t('auto.set.days')}
                          className="h-7 w-20 text-xs"
                          value={newThreshold[key] || ''}
                          onChange={(e) => setNewThreshold(prev => ({ ...prev, [key]: e.target.value  }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') addThreshold(key); }}
                          data-testid={`reminders-threshold-input-${key}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => addThreshold(key)}
                          data-testid={`reminders-threshold-add-${key}`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <Card data-testid="reminders-history-card">
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-4 h-4" />
                {t('auto.set.reminderHistory')}
                <Badge variant="secondary" className="ml-1">{history.total}</Badge>
              </CardTitle>
              <CardDescription>
                Audit log of every reminder the scanner has fired — who received it, when, and through which channel.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={historyFilter.channel || '__all__'}
                onValueChange={(v) => setHistoryFilter(prev => ({ ...prev, channel: v === '__all__' ? '' : v  }))}
              >
                <SelectTrigger className="h-8 w-40 text-xs" data-testid="reminders-history-channel-filter">
                  <SelectValue placeholder={t('auto.set.allChannels')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('auto.set.allChannels')}</SelectItem>
                  <SelectItem value="email">{t('auto.set.emailIndividual')}</SelectItem>
                  <SelectItem value="email_digest">{t('auto.set.emailDigest')}</SelectItem>
                  <SelectItem value="dashboard">{t('auto.set.dashboard')}</SelectItem>
                  <SelectItem value="google_calendar">{t('auto.set.googleCalendar')}</SelectItem>
                </SelectContent>
              </Select>
              <label className="text-xs text-slate-600 flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={historyFilter.only_failed}
                  onChange={(e) => setHistoryFilter(prev => ({ ...prev, only_failed: e.target.checked  }))}
                  data-testid="reminders-history-only-failed"
                />
                {t('auto.set.onlyFailures')}
              </label>
              <Button size="sm" variant="ghost" onClick={loadHistory} disabled={historyLoading} data-testid="reminders-history-refresh">
                <RefreshCw className={`w-3 h-3 ${historyLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="py-6 text-center text-sm text-slate-500">{t('auto.set.loading')}</div>
          ) : history.items.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500">
              {t('auto.set.noRemindersMatchTheCurrentFilter')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="reminders-history-table">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <th className="py-2 pr-2 font-semibold text-slate-600">{t('auto.set.when')}</th>
                    <th className="py-2 pr-2 font-semibold text-slate-600">{t('auto.set.channel')}</th>
                    <th className="py-2 pr-2 font-semibold text-slate-600">{t('auto.set.employee')}</th>
                    <th className="py-2 pr-2 font-semibold text-slate-600">{t('auto.set.document')}</th>
                    <th className="py-2 pr-2 font-semibold text-slate-600">{t('auto.set.expiry')}</th>
                    <th className="py-2 pr-2 font-semibold text-slate-600">{t('auto.set.threshold')}</th>
                    <th className="py-2 pr-2 font-semibold text-slate-600">{t('auto.set.recipient')}</th>
                    <th className="py-2 pr-2 font-semibold text-slate-600">{t('auto.set.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.items.map((it, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`reminders-history-row-${idx}`}>
                      <td className="py-1.5 pr-2 text-slate-600 whitespace-nowrap">{(it.sent_at || '').replace('T', ' ').slice(0, 16)}</td>
                      <td className="py-1.5 pr-2">
                        <Badge variant="outline" className="text-[10px] font-mono">{it.channel}</Badge>
                      </td>
                      <td className="py-1.5 pr-2 text-slate-900 font-medium">{it.employee_name}</td>
                      <td className="py-1.5 pr-2 text-slate-700">{it.document_label}</td>
                      <td className="py-1.5 pr-2 text-slate-600 whitespace-nowrap">{it.expiry_date}</td>
                      <td className="py-1.5 pr-2 text-slate-600">{it.threshold_days}d</td>
                      <td className="py-1.5 pr-2 text-slate-700">
                        <div>{it.recipient_name || it.recipient_email}</div>
                        {it.recipient_tier && <div className="text-[10px] text-slate-500">as {it.recipient_tier}</div>}
                      </td>
                      <td className="py-1.5 pr-2">
                        {it.success ? (
                          <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> sent</span>
                        ) : (
                          <span className="text-red-700 inline-flex items-center gap-1" title={it.error || ''}><XCircle className="w-3 h-3" /> failed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AssetReturnReminders settings={settings} canEdit={canEdit} onSave={save} />
    </div>
  );
};

export default RemindersSettings;
